import { Injectable, OnDestroy } from '@angular/core';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { TimberService } from '@logging/timber.service';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { ClientDiscoveryService } from 'app/platform/services/client-discovery/client-discovery.service';
import { FeatureToggleSettings } from "../../featuresToggleSettings/featuresToggleSettings.model";
import { ShellContextService } from 'app/services/shell-context/shell-context.service';
import { combineLatest, EMPTY, from, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { FeatureToggle } from '@shared/enums';
import { Utils } from '@shared/utils.service';
import { Context, PendoSDK } from '@itero/pendo-sdk';

@Injectable({
	providedIn: 'root',
})
export class PendoService implements OnDestroy {
	applicatioName = 'myItero.com';
	private readonly serviceName = 'PendoService';
	private readonly pendoSdkName = 'pendo-sdk';
	pendoSdk: any = null;
	initSubscription: Subscription;

	constructor(
		private readonly featuresToggleSettingsService: FeaturesToggleSettingsService,
		private readonly timber: TimberService,
		private readonly globalSettingService: GlobalSettingsService,
		private readonly shellContextService: ShellContextService,
		private readonly utilsService: Utils,
		private readonly clientDiscoveryService: ClientDiscoveryService
	) {}

	ngOnDestroy(): void {
		this.utilsService.clearObservablesSubscriptions(this);
	}

	async initializePendo(contactId, companyId) {
		const cswo = Object.keys(this.globalSettingService.get()?.companySoftwareOptionsKeyValuePair || {});

		const serviceData = await this.clientDiscoveryService.getClientApplication(this.pendoSdkName);
		if (serviceData) {
			const pendoPromise = import(/* webpackIgnore: true */ serviceData?.url);

			const pendoSdk$: Observable<PendoSDK> = from(pendoPromise).pipe(
				catchError((error) => {
					this.timber.error('Failed to load Pendo SDK', { module: this.serviceName, error });
					return EMPTY;
				}));

			this.initSubscription = combineLatest([
				this.featuresToggleSettingsService.getFeaturesToggleSettings(),
				pendoSdk$,
				this.shellContextService.selectedLanguage$
			]).pipe(
				filter(([featureFlags, pendoSdk, selectedLanguage]: [FeatureToggleSettings[], any, string]) =>
					featureFlags.some((ft) => ft.id === FeatureToggle.PendoToggle && ft.isActive) && pendoSdk),
				tap(([featureFlags, pendoSdk, selectedLanguage]: [FeatureToggleSettings[], any, string]) => {
					this.pendoSdk = pendoSdk;
					const pendoContext: Context = {
						contactId,
						companyId,
						appName: this.applicatioName,
						featureFlags,
						companySoftwareOptions: cswo,
						language: selectedLanguage
					};
					this.pendoSdk.manage(pendoContext,
						(message: string) => this.timber.error(message, { module: 'pendo-service' }));
				})
			).subscribe();
		}
	}

	closePendoInstance() {
		if (this.pendoSdk) {
			this.pendoSdk.manage(undefined, (message: string) => console.log(message));
		}
	}
}
